/**
 * "Autarch" — the AI that fills empty seats.
 *
 * Design goals, in order:
 *
 *   1. **Never embarrassing.** It must claim good systems, upgrade them, and buy tech in a
 *      sensible order. A bot that flails is worse than no bot.
 *   2. **Legible.** A human at the couch should be able to say "it went for the gas giants"
 *      — so the AI commits to a *plan* (a weighted preference over resource types and
 *      regions, chosen at match start from its seat's neighbourhood) rather than
 *      re-deciding its personality every turn.
 *   3. **Fast.** Scored heuristically, not by search. A whole AI turn is well under a
 *      millisecond, so three bots never stall the frame.
 *
 * Difficulty is expressed as decision noise plus a couple of capability gates, not as
 * cheating — the AI reads only what a player at the table can see.
 */

import { RELICS, SYSTEM_CLASSES } from './constants';
import { charterProgress, CHARTERS } from './charters';
import { checkAction, effectiveJumpRange, enumerateActions, straightIshPath } from './actions';
import { TECHS, prerequisiteMet, researchCost, trackLevel } from './tech';
import { Rng } from './rng';
import {
  claimTargets, colonyCount, effectiveHabitability, fleetCount, fleetsOf, playerOf,
  surveyTargets, systemDistance, systemOf, systemsOwnedBy, upgradeTargets,
} from './queries';
import type {
  GameAction, GameState, HexKey, PlayerId, ResourceKind, StarSystem, TechTrack,
} from './types';

/* ----------------------------------------------------------------- difficulty */

interface Profile {
  /** Random jitter added to every action score. Higher = sloppier. */
  noise: number;
  /** Fraction of its stock the AI is willing to commit to a contest. */
  contestAggression: number;
  /** Fraction of its Data the AI will spend on a charter it wants. */
  auctionAggression: number;
  /** Whether it bothers with megastructures and tier III tech. */
  playsLongGame: boolean;
}

const PROFILES: Record<GameState['settings']['aiDifficulty'], Profile> = {
  calm: { noise: 34, contestAggression: 0.25, auctionAggression: 0.3, playsLongGame: false },
  standard: { noise: 14, contestAggression: 0.45, auctionAggression: 0.5, playsLongGame: true },
  ruthless: { noise: 4, contestAggression: 0.7, auctionAggression: 0.7, playsLongGame: true },
};

/* ---------------------------------------------------------------------- plans */

/**
 * A seat's standing bias. Derived from the seed and the seat, so the same match always
 * produces the same personalities and a replay is faithful.
 */
export interface AiPlan {
  seat: PlayerId;
  name: string;
  /** Multiplier per resource, so a "Foundry" bot really does prefer Ore worlds. */
  appetite: Record<ResourceKind, number>;
  /** Tech track it opens with. */
  favouredTrack: TechTrack;
  /** How strongly it wants to push toward the Core. */
  corePull: number;
}

const PLAN_ARCHETYPES: readonly Omit<AiPlan, 'seat'>[] = [
  {
    name: 'Foundry Autarch', favouredTrack: 'industry', corePull: 0.8,
    appetite: { ore: 1.6, fuel: 1.0, biomass: 0.9, data: 0.8 },
  },
  {
    name: 'Seedship Autarch', favouredTrack: 'bio', corePull: 0.4,
    appetite: { ore: 0.9, fuel: 0.8, biomass: 1.7, data: 0.9 },
  },
  {
    name: 'Archive Autarch', favouredTrack: 'science', corePull: 1.2,
    appetite: { ore: 0.8, fuel: 0.9, biomass: 0.9, data: 1.7 },
  },
  {
    name: 'Lanewright Autarch', favouredTrack: 'drive', corePull: 1.0,
    appetite: { ore: 1.0, fuel: 1.5, biomass: 0.9, data: 1.0 },
  },
];

/** Stable per-seat plans for a match. */
export function makePlans(state: GameState): Map<PlayerId, AiPlan> {
  const rng = new Rng(state.settings.seed ^ 0x5eed);
  const archetypes = rng.shuffled(PLAN_ARCHETYPES);
  const plans = new Map<PlayerId, AiPlan>();
  state.players.forEach((player, index) => {
    const archetype = archetypes[index % archetypes.length]!;
    plans.set(player.id, { ...archetype, seat: player.id });
  });
  return plans;
}

/* ------------------------------------------------------------------ valuation */

/** What a system is worth to this seat, given its appetite. */
function systemWorth(state: GameState, system: StarSystem, plan: AiPlan): number {
  if (system.cls === 'deepSpace') return 0;

  const y = system.yield;
  let worth =
    y.ore * 10 * plan.appetite.ore +
    y.fuel * 10 * plan.appetite.fuel +
    y.biomass * 11 * plan.appetite.biomass +
    y.data * 12 * plan.appetite.data;

  // Habitability 2+ means it can become a Colony: double yield, population, and 3 VP.
  const hab = effectiveHabitability(state, system);
  if (hab >= 2) worth *= 1.5;
  if (hab >= 3) worth += 8;

  worth -= system.hazard * 5;
  if (system.relic && !system.relicTaken) {
    const payout = RELICS[system.relic].payout;
    worth += 30 + (payout.ore + payout.fuel + payout.biomass + payout.data) * 2;
  }
  if (system.region === 'core') worth += 14 * plan.corePull;
  if (system.region === 'midRim') worth += 5;

  return worth;
}

/** The best unclaimed system on the charted board, and what it is worth to this plan. */
function bestClaimable(
  state: GameState,
  plan: AiPlan,
): { id: HexKey; worth: number } | null {
  let best: { id: HexKey; worth: number } | null = null;
  for (const system of Object.values(state.systems)) {
    if (system.owner !== null || !system.surveyed) continue;
    if (system.cls === 'deepSpace' || system.habitability < 1) continue;
    const worth = systemWorth(state, system, plan);
    if (best === null || worth > best.worth) best = { id: system.id, worth };
  }
  return best;
}

/** How badly the seat wants a given tech right now. */
function techAppetite(state: GameState, seat: PlayerId, plan: AiPlan, profile: Profile): (id: keyof typeof TECHS) => number {
  const player = playerOf(state, seat);
  return (id) => {
    const t = TECHS[id];
    if (!prerequisiteMet(player, id)) return -Infinity;
    if (player.resources.data < researchCost(player, id)) return -Infinity;
    if (t.tier === 3 && !profile.playsLongGame) return -Infinity;

    let score = 44 + t.vp * 9;
    if (t.track === plan.favouredTrack) score += 22;

    // Tier I is nearly always correct early; tier III only once there is an engine to
    // amplify.
    if (t.tier === 1) score += 12;
    if (t.tier === 3 && colonyCount(state, seat) < 3) score -= 24;

    // Concrete synergies the bot can actually feel.
    if (id === 'industryI') score += colonyCount(state, seat) * 6;
    if (id === 'bioIII') score += systemsOwnedBy(state, seat).filter((s) => s.habitability === 1).length * 8;
    // Drive is only worth it while there is still something out there to reach.
    if ((id === 'driveI' || id === 'driveII') && bestClaimable(state, plan)) score += 10;
    if (id === 'industryII' && systemsOwnedBy(state, seat).length > 0) score += 6;

    score -= researchCost(player, id) * 2.5;
    return score;
  };
}

/* --------------------------------------------------------------- action choice */

/**
 * Score a candidate action. Everything is on one scale so the AI can compare a claim
 * against a research against a retreat without special-casing.
 */
function scoreAction(
  state: GameState,
  seat: PlayerId,
  plan: AiPlan,
  profile: Profile,
  action: GameAction,
): number {
  const player = playerOf(state, seat);
  const roundsLeft = state.settings.totalRounds - state.round;

  switch (action.kind) {
    case 'pass':
      // Only attractive when nothing else is, plus a nudge to bank Fuel late.
      return 8 + (player.resources.fuel < 2 ? 6 : 0);

    case 'claim': {
      const system = systemOf(state, action.systemId);
      if (!system) return -Infinity;
      // Claiming is the single highest-leverage action in the game: VP, yield, and
      // region majority all at once.
      return 120 + systemWorth(state, system, plan) * 0.9;
    }

    case 'upgrade': {
      const system = systemOf(state, action.systemId);
      if (!system) return -Infinity;
      // Worth more the longer it has to pay out.
      return 100 + systemWorth(state, system, plan) * 0.6 + roundsLeft * 3;
    }

    case 'terraform': {
      const system = systemOf(state, action.systemId);
      if (!system) return -Infinity;
      // The good case is unlocking an upgrade on a habitability-1 world.
      const unlocksColony =
        system.development === 'outpost' && effectiveHabitability(state, system) === 1;
      let score = unlocksColony ? 86 : 24;
      if (system.development === 'colony' && system.habitability === 2) score = 52; // → prime
      score += system.hazard * 4;
      return score;
    }

    case 'research': {
      const appetite = techAppetite(state, seat, plan, profile);
      return appetite(action.techId);
    }

    case 'deployFleet': {
      const fleets = fleetCount(state, seat);
      if (fleets >= 4) return 6;
      // Fleets exist to claim with, so want one only if there is something to go and take.
      const target = bestClaimable(state, plan);
      const hunger = target ? 1 : 0.3;
      return (fleets <= 1 ? 74 : 46 - fleets * 8) * hunger;
    }

    case 'survey': {
      const known = claimTargets(state, seat).length;
      const unexplored = surveyTargets(state, seat).length;
      // Survey hard while there is nothing worth claiming; taper off once there is.
      let score = 58 - known * 16 + Math.min(unexplored, 4) * 3;
      if (roundsLeft <= 2) score -= 30; // no time to exploit new knowledge
      const cartographer = player.charters.includes('cartographer');
      if (cartographer) {
        const [done, need] = charterProgress(state, player, 'cartographer');
        if (done < need) score += 22;
      }
      return score;
    }

    case 'move': {
      const fleet = state.fleets[action.fleetId];
      if (!fleet) return -Infinity;
      const destination = action.path[action.path.length - 1]!;
      const target = systemOf(state, destination);
      if (!target) return -Infinity;

      // Landing directly on something claimable is the whole point.
      const claimable =
        target.owner === null &&
        target.surveyed &&
        target.cls !== 'deepSpace' &&
        target.habitability >= 1;
      if (claimable) return 92 + systemWorth(state, target, plan) * 0.5 - action.path.length * 2;

      // Otherwise value closing distance on the best prize.
      const best = bestClaimable(state, plan);
      if (!best) return 4;
      const before = systemDistance(fleet.at, best.id);
      const after = systemDistance(destination, best.id);
      const progress = before - after;
      if (progress <= 0) return 2;
      return 34 + progress * 12 - action.path.length;
    }

    case 'trade': {
      // Only sensible to unblock a specific purchase. Value the resource we are short of.
      const bottleneck = shortfallResource(state, seat, plan);
      if (bottleneck !== action.take) return -20;
      if (player.resources[action.give] < 6) return -12; // do not gut a stock we need
      return 40;
    }

    case 'megastructure': {
      if (!profile.playsLongGame) return -Infinity;
      // 8 VP is huge, but so is 8 Ore + 4 Fuel + 4 Data. Only when genuinely flush.
      if (roundsLeft > 5) return -10;
      return 118;
    }

    case 'advancePhase':
    case 'submitBid':
      return -Infinity;
  }
}

/** Which resource is most blocking this seat's next intended purchase. */
function shortfallResource(state: GameState, seat: PlayerId, plan: AiPlan): ResourceKind {
  const player = playerOf(state, seat);
  const wantsClaim = claimTargets(state, seat).length > 0;
  const wantsUpgrade = upgradeTargets(state, seat).length > 0;

  if (wantsClaim && player.resources.ore < 3) return 'ore';
  if (wantsUpgrade && player.resources.ore < 3) return 'ore';
  if (wantsUpgrade && player.resources.biomass < 2) return 'biomass';
  if (player.resources.fuel < 2) return 'fuel';
  if (player.resources.data < 3) return 'data';

  // Nothing urgent — top up whatever the plan likes most.
  let best: ResourceKind = 'ore';
  for (const kind of ['ore', 'fuel', 'biomass', 'data'] as const) {
    if (plan.appetite[kind] > plan.appetite[best]) best = kind;
  }
  return best;
}

/**
 * Choose this seat's single Command-phase action.
 *
 * Returns `pass` if nothing scores above the pass baseline, which is a real decision:
 * banking Fuel and initiative beats spending 2 CP on a bad claim.
 */
export function chooseAction(
  state: GameState,
  seat: PlayerId,
  plan: AiPlan,
): GameAction {
  const profile = PROFILES[state.settings.aiDifficulty];
  const candidates = enumerateActions(state, seat);

  // Deterministic jitter: seeded on the seat and the action count so a replay of the
  // same match makes the same "random" choices.
  const rng = new Rng((state.rngState ^ (seat + 1) * 0x9e3779b9 ^ state.round * 31) >>> 0);

  let best: GameAction = { kind: 'pass', player: seat };
  let bestScore = -Infinity;

  for (const candidate of candidates) {
    // `enumerateActions` bounds targets to plausible ones, but affordability and CP
    // change as the round goes on — so gate on the authoritative checker.
    if (!checkAction(state, candidate).ok) continue;

    const score = scoreAction(state, seat, plan, profile, candidate) +
      (rng.next() - 0.5) * profile.noise;
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

/* --------------------------------------------------------------------- bidding */

/**
 * How much Fuel to commit to a contest.
 *
 * The bot estimates what the hex is worth to it, converts that into a fuel ceiling, and
 * then bids just enough to beat the strongest plausible rival — capped by its aggression
 * profile so it cannot bankrupt itself over a rock.
 */
export function chooseContestBid(
  state: GameState,
  seat: PlayerId,
  plan: AiPlan,
  systemId: HexKey,
): number {
  const profile = PROFILES[state.settings.aiDifficulty];
  const player = playerOf(state, seat);
  const system = systemOf(state, systemId);
  if (!system) return 0;

  const myFleets = fleetsOf(state, seat).filter((f) => f.at === systemId).length;
  const rivalFleets = Math.max(
    0,
    ...state.players
      .filter((p) => p.id !== seat)
      .map((p) => fleetsOf(state, p.id).filter((f) => f.at === systemId).length),
  );

  const worth = systemWorth(state, system, plan);
  const defending = system.owner === seat;

  // Convert worth into a willingness-to-pay in Fuel. ~55 worth ≈ 1 Fuel.
  let ceiling = Math.floor((worth / 55) * (defending ? 1.6 : 1.0));
  ceiling = Math.min(ceiling, Math.floor(player.resources.fuel * profile.contestAggression));

  // Only pay for a bid that could actually flip the result.
  const deficit = Math.max(0, (rivalFleets - myFleets) * 2 + (defending ? -1 : 1));
  const target = Math.min(ceiling, deficit + 1);

  return Math.max(0, Math.min(player.resources.fuel, target));
}

/** How much Data to commit to a charter. */
export function chooseAuctionBid(
  state: GameState,
  seat: PlayerId,
  plan: AiPlan,
): number {
  const profile = PROFILES[state.settings.aiDifficulty];
  const player = playerOf(state, seat);
  const bid = state.activeBid;
  if (!bid || !bid.charterId) return 0;

  const charter = CHARTERS[bid.charterId];
  const [done, need] = charterProgress(state, player, bid.charterId);
  const closeness = need > 0 ? Math.min(1, done / need) : 0;

  // Worth = printed VP scaled by how nearly it is already satisfied, since a charter the
  // bot cannot finish is worth nothing at all.
  const roundsLeft = state.settings.totalRounds - state.round;
  const reachable = closeness + Math.min(0.5, roundsLeft * 0.08);
  const worth = charter.vp * reachable;

  // Charters aligned with the plan are worth overpaying for slightly.
  const aligned = charterMatchesPlan(bid.charterId, plan) ? 1.35 : 1.0;

  let amount = Math.round(worth * aligned * 0.9);
  amount = Math.min(amount, Math.floor(player.resources.data * profile.auctionAggression));

  // Keep enough Data to finish a tech already in reach.
  const reserve = state.techMarket.some((id) => prerequisiteMet(player, id)) ? 2 : 0;
  amount = Math.min(amount, Math.max(0, player.resources.data - reserve));

  return Math.max(0, amount);
}

function charterMatchesPlan(id: keyof typeof CHARTERS, plan: AiPlan): boolean {
  switch (id) {
    case 'gasBaron': return plan.appetite.fuel > 1.2;
    case 'ironWorks': return plan.appetite.ore > 1.2;
    case 'archivist':
    case 'cartographer': return plan.appetite.data > 1.2;
    case 'greenThumb':
    case 'settler':
    case 'population': return plan.appetite.biomass > 1.2;
    case 'pathfinder': return plan.favouredTrack === 'drive';
    case 'deepCore': return plan.corePull >= 1;
    case 'terraformer': return plan.favouredTrack === 'bio';
    default: return false;
  }
}

/* ---------------------------------------------------------------- explanation */

/**
 * A one-line, human-readable justification for the AI's move, shown in the log so the
 * couch can follow what the bots are doing. Purely cosmetic.
 */
export function describeAction(state: GameState, action: GameAction): string {
  switch (action.kind) {
    case 'claim': {
      const system = systemOf(state, action.systemId);
      return system ? `stakes ${SYSTEM_CLASSES[system.cls].name} ${system.id}` : 'stakes a claim';
    }
    case 'upgrade': return `grows ${action.systemId} into a Colony`;
    case 'terraform': return `terraforms ${action.systemId}`;
    case 'research': return `studies ${TECHS[action.techId].name}`;
    case 'move': return `repositions toward ${action.path[action.path.length - 1] ?? '?'}`;
    case 'survey': return `charts ${action.systemId}`;
    case 'deployFleet': return `commissions a fleet at ${action.systemId}`;
    case 'trade': return `trades ${action.give} for ${action.take}`;
    case 'megastructure': return `begins a ${action.pick}`;
    case 'pass': return 'holds position';
    default: return 'waits';
  }
}

/** How long the bot should appear to "think", so its turn is readable at the couch. */
export function thinkDelayMs(state: GameState): number {
  const range = effectiveJumpRange(state, 0); // cheap, keeps the signature honest
  return 420 + Math.min(260, range * 40);
}

/** Convenience for the game loop: is this seat AI-controlled? */
export function isAiSeat(state: GameState, seat: PlayerId): boolean {
  return playerOf(state, seat).kind === 'ai';
}

/** Re-exported so callers can build a move without importing actions directly. */
export { straightIshPath, trackLevel };
